import type { Area, AreaElementType, GeoStatus, Responsibility } from '../api/types';

export const areaStatusLabels: Record<Area['status'], string> = { ACTIVE: 'Aktiv', INACTIVE: 'Deaktiviert', ARCHIVED: 'Archiviert' };

export const geoStatusLabels: Record<GeoStatus, string> = {
  UNPROCESSED: 'Unverarbeitet',
  VALID: 'Eindeutig',
  AMBIGUOUS: 'Mehrdeutig',
  INVALID_POSTAL_CODE: 'PLZ ungültig',
  CITY_MISMATCH: 'PLZ/Ort widersprüchlich',
  ADDRESS_INCOMPLETE: 'Adresse unvollständig',
  NOT_FOUND: 'Nicht gefunden',
  MANUAL_REVIEW: 'Manuelle Prüfung',
};

export const reviewStatuses: GeoStatus[] = [
  'CITY_MISMATCH',
  'INVALID_POSTAL_CODE',
  'NOT_FOUND',
  'ADDRESS_INCOMPLETE',
  'AMBIGUOUS',
  'MANUAL_REVIEW',
];

// Filter der Geo-Klärung für wahrscheinlich zugeordnete, unbestätigte Leads (kein Geo-Status – die Leads sind eindeutig).
export const assumedFilter = 'ASSUMED';
export const assumedLabel = 'Wahrscheinlich zugeordnet';

// isAssumed: nur nach Wahrscheinlichkeit zugeordnet und unbestätigt (Vermerk ASSUMED_LOCATION).
export function isAssumed(l: { geo_status: string; geo_source: string; geo_details?: unknown }): boolean {
  const problems = (l.geo_details as { problems?: string[] } | null | undefined)?.problems;
  return l.geo_status === 'VALID' && l.geo_source === 'AUTO' && !!problems?.includes('ASSUMED_LOCATION');
}

export function geoTone(status: GeoStatus): 'ok' | 'warn' | 'error' | 'muted' {
  if (status === 'VALID') return 'ok';
  if (status === 'UNPROCESSED') return 'muted';
  if (status === 'AMBIGUOUS' || status === 'ADDRESS_INCOMPLETE' || status === 'MANUAL_REVIEW') return 'warn';
  return 'error';
}

export const problemLabels: Record<string, string> = {
  POSTAL_CODE_MISSING: 'PLZ fehlt',
  POSTAL_CODE_INVALID: 'PLZ ungültig',
  POSTAL_CODE_UNKNOWN: 'PLZ unbekannt',
  CITY_MISSING: 'Ort fehlt',
  CITY_NOT_MATCHING: 'Ort passt nicht zur PLZ',
  CITY_FUZZY_MATCH: 'Ort nur ähnlich geschrieben',
  MUNICIPALITY_AMBIGUOUS: 'Mehrere Gemeinden möglich (Kreis steht fest, falls die PLZ in einem Kreis liegt)',
  ASSUMED_LOCATION: 'Wahrscheinlichste Zuordnung – bitte im Gespräch bestätigen',
  COUNTY_AMBIGUOUS: 'Mehrere Landkreise möglich',
  STREET_MISSING: 'Straße fehlt',
  HOUSE_NUMBER_MISSING: 'Hausnummer fehlt',
  AREA_AMBIGUOUS: 'Mehrere Gebiete möglich',
};

export const elementTypeLabels: Record<AreaElementType, string> = {
  FEDERAL_STATE: 'Bundesland',
  COUNTY: 'Landkreis',
  MUNICIPALITY: 'Gemeinde',
  POSTAL_CODE: 'PLZ',
};

export const responsibilityLabels: Record<Responsibility, string> = {
  FIELD_SALES: 'Außendienst',
  TELESALES: 'Telefonakquise',
  SUPPORT: 'Support',
  SUPERVISION: 'Supervision',
};

export const sourceLabels: Record<string, string> = {
  AUTO: 'automatisch',
  MANUAL: 'manuell',
  IMPORT: 'Import',
  AI: 'KI',
  RULE: 'Regelgebiet',
};

export const numberFormat = new Intl.NumberFormat('de-DE');

// ruleText: Regel eines Regelgebiets (B-26) in einem Satz – leer bei geografischen Gebieten.
export function ruleText(a: Pick<Area, 'kind' | 'rule_min_price_eur'>): string {
  if (a.kind !== 'RULE' || a.rule_min_price_eur == null) return '';
  return `Regelgebiet: alle Leads mit einem Objekt ab ${numberFormat.format(a.rule_min_price_eur)} € Preisvorstellung, bundesweit. Hat Vorrang vor den geografischen Gebieten – diese Leads stehen nur hier.`;
}
